import React, { useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProfitMarginChart = ({ data = [] }) => {
  const [selectedMargin, setSelectedMargin] = useState('all'); // all, gross, operating, net

  const formatPercentage = (value) => {
    return `${value?.toFixed(1)}%`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-3 card-shadow">
          <p className="text-sm font-medium text-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <p key={index} className="text-sm flex items-center justify-between">
              <span style={{ color: entry?.color }}>
                {entry?.dataKey === 'grossMargin' ? 'Gross Margin' : 
                 entry?.dataKey === 'operatingMargin' ? 'Operating Margin' : 'Net Margin'}:
              </span>
              <span className="ml-2 font-medium" style={{ color: entry?.color }}>
                {formatPercentage(entry?.value)}
              </span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const getVisibleMargins = () => {
    switch (selectedMargin) {
      case 'gross':
        return ['grossMargin'];
      case 'operating':
        return ['operatingMargin'];
      case 'net':
        return ['netMargin'];
      default:
        return ['grossMargin', 'operatingMargin', 'netMargin'];
    }
  };

  const getLatestMargins = () => {
    if (data?.length === 0) return { gross: 0, operating: 0, net: 0 };
    const latest = data?.[data?.length - 1];
    return {
      gross: latest?.grossMargin,
      operating: latest?.operatingMargin,
      net: latest?.netMargin
    };
  };

  const getAverageMargins = () => {
    if (data?.length === 0) return { gross: 0, operating: 0, net: 0 };
    const totals = data?.reduce((acc, item) => ({
      gross: acc?.gross + item?.grossMargin,
      operating: acc?.operating + item?.operatingMargin,
      net: acc?.net + item?.netMargin
    }), { gross: 0, operating: 0, net: 0 });
    
    return {
      gross: totals?.gross / data?.length,
      operating: totals?.operating / data?.length,
      net: totals?.net / data?.length
    };
  };

  const visibleMargins = getVisibleMargins();
  const latestMargins = getLatestMargins();
  const averageMargins = getAverageMargins();

  return (
    <div className="bg-card border border-border rounded-lg p-6 card-shadow">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
            <Icon name="PieChart" size={20} className="text-warning" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Profit Margin Analysis</h3>
            <p className="text-sm text-muted-foreground">Quarterly profit margin trends</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <div className="flex bg-muted rounded-lg p-1">
            <button
              onClick={() => setSelectedMargin('all')}
              className={`px-2 py-1 text-xs font-medium rounded-md transition-colors ${
                selectedMargin === 'all' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setSelectedMargin('gross')}
              className={`px-2 py-1 text-xs font-medium rounded-md transition-colors ${
                selectedMargin === 'gross' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Gross
            </button>
            <button
              onClick={() => setSelectedMargin('operating')}
              className={`px-2 py-1 text-xs font-medium rounded-md transition-colors ${
                selectedMargin === 'operating' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Operating
            </button>
            <button
              onClick={() => setSelectedMargin('net')}
              className={`px-2 py-1 text-xs font-medium rounded-md transition-colors ${
                selectedMargin === 'net' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Net
            </button>
          </div>
          <Button variant="ghost" size="sm" iconName="Calculator">
            Calculate
          </Button>
        </div>
      </div>
      <div className="h-80 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
            <defs>
              <linearGradient id="grossGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="operatingGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="netGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis 
              dataKey="quarter" 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: 'currentColor' }}
              className="text-muted-foreground"
            />
            <YAxis 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: 'currentColor' }}
              className="text-muted-foreground"
              tickFormatter={formatPercentage}
              domain={[0, 60]}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              verticalAlign="bottom" 
              height={36}
              wrapperStyle={{ paddingTop: '20px' }}
            />
            {visibleMargins?.includes('grossMargin') && (
              <Area 
                type="monotone" 
                dataKey="grossMargin" 
                stroke="#3B82F6"
                strokeWidth={2}
                fill="url(#grossGradient)"
                name="Gross Margin"
                dot={{ fill: '#3B82F6', strokeWidth: 0, r: 4 }}
                activeDot={{ r: 6, stroke: '#3B82F6', strokeWidth: 2 }}
              />
            )}
            {visibleMargins?.includes('operatingMargin') && (
              <Area 
                type="monotone" 
                dataKey="operatingMargin" 
                stroke="#10B981"
                strokeWidth={2}
                fill="url(#operatingGradient)"
                name="Operating Margin"
                dot={{ fill: '#10B981', strokeWidth: 0, r: 4 }}
                activeDot={{ r: 6, stroke: '#10B981', strokeWidth: 2 }}
              />
            )}
            {visibleMargins?.includes('netMargin') && (
              <Area 
                type="monotone" 
                dataKey="netMargin" 
                stroke="#8B5CF6"
                strokeWidth={2}
                fill="url(#netGradient)"
                name="Net Margin"
                dot={{ fill: '#8B5CF6', strokeWidth: 0, r: 4 }}
                activeDot={{ r: 6, stroke: '#8B5CF6', strokeWidth: 2 }}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-border">
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-2">Current Quarter</h4>
          <div className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-primary">Gross Margin:</span>
              <span className="font-medium">{formatPercentage(latestMargins?.gross)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-success">Operating Margin:</span>
              <span className="font-medium">{formatPercentage(latestMargins?.operating)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-purple-600">Net Margin:</span>
              <span className="font-medium">{formatPercentage(latestMargins?.net)}</span>
            </div>
          </div>
        </div>
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-2">Average (7 Quarters)</h4>
          <div className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-primary">Gross Margin:</span>
              <span className="font-medium">{formatPercentage(averageMargins?.gross)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-success">Operating Margin:</span>
              <span className="font-medium">{formatPercentage(averageMargins?.operating)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-purple-600">Net Margin:</span>
              <span className="font-medium">{formatPercentage(averageMargins?.net)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfitMarginChart;